/**
 * Redlines in words, for an agent: what each mark is, what it says, and where
 * it sits. On a model plane that is model space (project units, Z-up, shown
 * in feet and inches), so a mark can be matched to what the model has there.
 * Plane point (x, y) sits at origin + scale · (x · right − y · up), with
 * right = up × normal (`app/bear-viewer/model-plane.ts`).
 */
import type { Plane, PlaneRecord } from "./annotations";
import { markText } from "./annotations";

type Vec3 = [number, number, number];

/** A project unit is a 32nd of an inch. */
const UNITS_PER_INCH = 32;

const isVec3 = (v: unknown): v is Vec3 =>
  Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === "number");

interface ModelFrame {
  origin: Vec3;
  normal: Vec3;
  up: Vec3;
  scale: number;
  render?: { exportName?: string | null; props?: unknown };
}

function modelFrame(plane: Plane): ModelFrame | null {
  const frame = plane.frame as Partial<ModelFrame> | null;
  return frame &&
    isVec3(frame.origin) &&
    isVec3(frame.normal) &&
    isVec3(frame.up) &&
    typeof frame.scale === "number"
    ? (frame as ModelFrame)
    : null;
}

function length(units: number): string {
  const inches = Math.round(Math.abs(units) / UNITS_PER_INCH);
  const sign = units < 0 ? "-" : "";
  return `${sign}${Math.floor(inches / 12)}'-${inches % 12}"`;
}

function cross([ax, ay, az]: Vec3, [bx, by, bz]: Vec3): Vec3 {
  return [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
}

function toModel(frame: ModelFrame, x: number, y: number): Vec3 {
  const right = cross(frame.up, frame.normal);
  return [0, 1, 2].map(
    (i) => frame.origin[i]! + frame.scale * (x * right[i]! - y * frame.up[i]!),
  ) as Vec3;
}

const at = ([x, y, z]: Vec3) =>
  `(x ${length(x)}, y ${length(y)}, z ${length(z)})`;

/** The plane itself: where it faces and what was rendered on it. */
export function describePlane(plane: Plane): string {
  const frame = modelFrame(plane);
  if (!frame) return `Markup plane ${plane.id}.`;
  const facing = frame.normal.map((n) => Math.round(n * 100) / 100).join(", ");
  const render = frame.render?.exportName
    ? ` It was drawn over the ${frame.render.exportName} export.`
    : "";
  return `Markup plane ${plane.id}, in model space: through ${at(frame.origin)}, facing (${facing}).${render}`;
}

/** One mark: kind, lettering, and its extent on the plane. */
export function describeMark(plane: Plane, mark: PlaneRecord): string {
  const props = (mark.props ?? {}) as {
    w?: unknown;
    h?: unknown;
    geo?: unknown;
  };
  const kind = typeof props.geo === "string" ? props.geo : String(mark.type);
  const x = Number(mark.x ?? 0);
  const y = Number(mark.y ?? 0);
  const w = typeof props.w === "number" ? props.w : 0;
  const h = typeof props.h === "number" ? props.h : 0;
  const text = markText(mark);
  const says = text ? ` reading "${text}"` : "";
  const frame = modelFrame(plane);
  if (!frame) return `${kind}${says} at plane (${x}, ${y}), ${w} × ${h}`;
  const where =
    w && h
      ? `spanning ${at(toModel(frame, x, y))} to ${at(toModel(frame, x + w, y + h))}, ${length(w * frame.scale)} × ${length(h * frame.scale)} on the plane`
      : `at ${at(toModel(frame, x, y))}`;
  return `${kind}${says}, ${where}`;
}

/** Every drawn mark on the plane, one line each, this thread's marked. */
export function describeMarks(plane: Plane, current?: string): string {
  return Object.values(plane.records)
    .filter(
      (record) =>
        record.typeName === "shape" &&
        String(record.parentId ?? "").startsWith("page:"),
    )
    .map(
      (record) =>
        `${record.id === current ? "* " : "- "}${describeMark(plane, record)}`,
    )
    .join("\n");
}
